import React from "react";
import gql from "graphql-tag";
import { Link, Location, LocationContext } from "@reach/router";
import { oc } from "ts-optchain";
import styled from "@emotion/styled";
import { BrandCircle } from "@organisms/brandCircleComponents/BrandCircle";
import { Box, Text, Flex, H3, IBoxProps, TouchableOpacity, TimesIcon } from "@atoms";
import { useStoreAppValue } from "@hooks";
import { renderIf, constants, RailsUrl } from "@services";

// == Types ================================================================

interface IProps extends IBoxProps {
  brand: $FixMe;
  linkBrand: boolean;
  distance?: string | null;
  address?: string;
  showCloseButton?: boolean;
  onCloseButtonClicked?: () => void;
  locationId?: string;
  sponsored?: boolean;
}
interface IBrandSearchItemProps {
  brand: $FixMe;
  distance?: string | null;
  address?: string;
  showCloseButton?: boolean;
  onCloseButtonClicked?: () => void;
  sponsored?: boolean;
}

interface IBrandSearchListItemGivesProps {
  enabled: boolean;
  label: string;
  value: string;
}

// == Constants ============================================================
// FRAGMENT should use BrandCircleFragment
const FRAGMENTS = {
  fields: gql`
    fragment BrandSearchListItemFragment on Brand {
      id
      name
      logoImage
      location(coordinates: $coordinates) {
        id
        address
      }
      shopLink {
        id
        giveAmount
      }
      giftcardLink {
        id
        giveAmount
      }
      linkedCardLink {
        id
        giveAmount
        baselineGiveAmount
        positiveGiveAmount
      }
      merchant {
        id
        department
      }
      ...BrandCircleFragment
    }
    ${BrandCircle.fragments.fields}
  `,
};

const DEFAULT_PROPS = {
  linkBrand: false,
  address: null,
  distance: null,
  showCloseButton: false,
  onCloseButtonClicked: null,
  sponsored: false,
};

// == Component ============================================================

const BrandSearchListItemGives = ({ enabled, label, value }: IBrandSearchListItemGivesProps) => {
  return (
    <>
      <Text color={enabled ? "primary" : "default"} display="block" fontSize={2}>
        {value}
      </Text>
      <Text fontSize={0}>{label}</Text>
    </>
  );
};

const BrandSearchItem = ({
  distance,
  address,
  brand,
  showCloseButton,
  onCloseButtonClicked,
  sponsored,
  ...rest
}: IBrandSearchItemProps) => {
  const { currency } = useStoreAppValue();
  const department = oc(brand).merchant.department();
  const shopGiveAmount = oc(brand).shopLink.giveAmount("-");
  const giftcardGiveAmount = oc(brand).giftcardLink.giveAmount("-");
  const hasLinkedCard = !!oc(brand).linkedCardLink.id();
  const linkedCardGiveAmount =
    oc(brand).linkedCardLink.giveAmount() || oc(brand).linkedCardLink.baselineGiveAmount("-");
  return (
    <Flex justifyContent="space-between" {...rest}>
      <Flex>
        <Box maxWidth={100}>
          <BrandCircle brand={brand} imageWidth={40} pad={7} />
        </Box>
        <Box flex={1} ml={3}>
          <Flex>
            <H3>{brand.name}</H3>
            {sponsored && (
              <ESponsorText color="notification" fontSize={0} mx={2} px={2}>
                Sponsored
              </ESponsorText>
            )}
          </Flex>
          {renderIf(department)(<Text display="block">{department}</Text>)}
          <Text fontSize={2}>{address}</Text>
          <Flex mt={2}>
            <Box mr={3}>
              <BrandSearchListItemGives
                enabled={shopGiveAmount !== "-"}
                label={constants.SEARCH_FILTERS_TYPE_NAMES.ONLINE}
                value={shopGiveAmount}
              />
            </Box>
            <Box mr={3}>
              <BrandSearchListItemGives
                enabled={giftcardGiveAmount !== "-"}
                label={constants.SEARCH_FILTERS_TYPE_NAMES.GIFTCARD}
                value={giftcardGiveAmount}
              />
            </Box>
            {renderIf(currency !== "CAD")(
              <Box>
                <BrandSearchListItemGives
                  enabled={hasLinkedCard}
                  label={constants.SEARCH_FILTERS_TYPE_NAMES.LINKED_CARD}
                  value={linkedCardGiveAmount}
                />
              </Box>
            )}
          </Flex>
        </Box>
      </Flex>
      {distance && (
        <Box pl={1}>
          <Text color="primary" fontSize={0}>
            {distance}
          </Text>
        </Box>
      )}
      {showCloseButton && (
        <Box pl={1}>
          <TouchableOpacity onClick={(e) => onCloseButtonClicked(e)}>
            <TimesIcon />
          </TouchableOpacity>
        </Box>
      )}
    </Flex>
  );
};

const BrandSearchListItem = ({
  brand,
  linkBrand,
  address,
  distance,
  showCloseButton,
  onCloseButtonClicked,
  locationId,
  sponsored,
  ...rest
}: IProps) => {
  if (linkBrand) {
    return (
      <Location>
        {({ location }: LocationContext) => {
          return (
            <ELink
              to={`${location.pathname}${RailsUrl.brandModalUrl({
                id: brand.id,
                locationId,
              })}`}
            >
              <BrandSearchItem
                address={address}
                brand={brand}
                distance={distance}
                showCloseButton={showCloseButton}
                sponsored={sponsored}
                onCloseButtonClicked={onCloseButtonClicked}
                {...rest}
              />
            </ELink>
          );
        }}
      </Location>
    );
  }

  return (
    <BrandSearchItem
      address={address}
      brand={brand}
      distance={distance}
      showCloseButton={showCloseButton}
      sponsored={sponsored}
      onCloseButtonClicked={onCloseButtonClicked}
      {...rest}
    />
  );
};

BrandSearchListItem.fragments = FRAGMENTS;
BrandSearchListItem.defaultProps = DEFAULT_PROPS;
export default BrandSearchListItem;

// == Styles ===============================================================

const ELink = styled(Link)`
  text-decoration: none;
`;

const ESponsorText = styled(Text)`
  border: ${({ theme }) => theme.borders.sponsor};
  border-radius: ${({ theme }) => `${theme.radii.modal}px`};
  display: flex;
  align-self: flex-start;
`;
